import React, { useEffect, useMemo, useState } from 'react';
import index_items from "../jsons/index_items.json";
import index_mobs from "../jsons/index_mobs.json";
import index_maps from "../jsons/index_maps.json";
import mvps from "../jsons/mvps.json";
import minibosses from "../jsons/minibosses.json";
import worldmapData from "../jsons/worldmap.json";
import epiDiff from "../jsons/epi_diff.json";
import filesUsed from "../jsons/files_used.json";

/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/
/* EPISODE DETAILS VIEW                */
/* Shared by App.js (desktop) and AppMobile.js (mobile) - prop driven, no layout assumptions */
/*%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%*/

/* Full chronological order (worldmap.json episodeTimeline is the complete list; "default" is the test set and never a "previous" episode) */
export const FULL_EPISODE_ORDER = worldmapData.episodeTimeline.filter((ep) => ep !== "default");

/* Labels mirror the Episode dropdown in App.js */
export const EPISODE_LABELS = {
  "default": "Test",
  "00": "Ep.00 - Alpha Era",
  "0": "Ep.0 - Beta Era",
  "1": "Ep.1 - Start of an Adventure",
  "1-5": "Ep.1.5 - Attack of the Ancient",
  "2": "Ep.2 - Lutie",
  "3": "Ep.3 - Comodo",
  "4": "Ep.4 - Turtle Island + WoE",
  "5": "Ep.5 - Juno",
  "6": "Ep.6 - Amatsu",
  "6-2": "Ep.6.2 - Kunlun",
  "7": "Ep.7 - Umbala",
  "7-1": "Ep.7.1 - Nifflheim",
  "8-1": "Ep.8.1 - Louyang",
  "8-2": "Ep.8.2 - Ayothaya",
  "10-1": "Ep.10.1 - Einbroch",
  "10-2": "Ep.10.2 - Lighthalzen",
  "10-3": "Ep.10.3 - Noghaltz",
  "10-4": "Ep.10.4 - Hugel",
  "11-1": "Ep.11.1 - Rachel",
  "11-2": "Ep.11.2 - Veins",
  "11-3": "Ep.11.3 - Nameless Island",
  "12": "Ep.12~13 - Satan Morroc Era",
  "13-1": "Ep.13.1 - Ash Vacuum",
  "13-2": "Ep.13.2 - Into the Unknown",
};
export const episodeLabel = (ep) => EPISODE_LABELS[ep] || `Ep.${ep}`;

/* Closest AVAILABLE episode strictly before the current one (walks FULL_EPISODE_ORDER backwards). null = nothing before it */
export const getPreviousEpisode = (currentEpisode, availableEpisodes) => {
  const idx = FULL_EPISODE_ORDER.indexOf(currentEpisode);
  if (idx < 0) return null;
  for (let i = idx - 1; i >= 0; i--) {
    if (availableEpisodes.has(FULL_EPISODE_ORDER[i])) return FULL_EPISODE_ORDER[i];
  }
  return null;
};

/* Position of an episode in the timeline, for "introduced between prev and current" checks (worldmap towns/intro data) */
const epPos = (ep) => FULL_EPISODE_ORDER.indexOf(ep);

/* Cards live in the 4001-4699 ID range (4700+ are enchant stones - "Strength1" etc.).
   Name matching was evaluated and rejected: it hits Old_Card_Album, Cardinal_Jewel, TCG_Card, Blue_Card_x, Cardo... while every real card sits in the range */
export const CARD_ID_MIN = 4001;
export const CARD_ID_MAX = 4699;
export const isCardID = (id) => { const n = parseInt(id, 10); return n >= CARD_ID_MIN && n <= CARD_ID_MAX; };

/* Manual notes - one optional JSON per episode in app/src/episode_notes/episode_notes_<ep>.json (see _TEMPLATE.json there) */
const notesContext = require.context('../episode_notes', false, /^\.\/episode_notes_[^/]+\.json$/);
export const getManualNotes = (ep) => {
  const key = `./episode_notes_${ep}.json`;
  return notesContext.keys().includes(key) ? notesContext(key) : null;
};

/* Loads the previous episode's DBs on demand (async import = cannot be a useMemo). Cleared while loading so the delta never mixes two episodes */
export const usePreviousEpisodeData = (prevEpisode) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    if (prevEpisode === null) return undefined;
    (async () => {
      try {
        const [mobDB, itemDB, spawn, droppedBy] = await Promise.all([
          import(`../jsons/${prevEpisode}/mob_db_${prevEpisode}.json`),
          import(`../jsons/${prevEpisode}/item_db_${prevEpisode}.json`),
          import(`../jsons/${prevEpisode}/mob_spawn_${prevEpisode}.json`),
          import(`../jsons/${prevEpisode}/dropped_by_${prevEpisode}.json`),
        ]);
        if (!cancelled) setData({ episode: prevEpisode, mobDB: mobDB.default, itemDB: itemDB.default, spawn: spawn.default, droppedBy: droppedBy.default });
      } catch (e) {
        if (!cancelled) setData({ episode: prevEpisode, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [prevEpisode]);
  return data;
};

/* Name helpers - same fallback chain the search uses */
const mobName = (mob, lang) => (index_mobs[mob.ID] && index_mobs[mob.ID][lang]) || (index_mobs[mob.ID] && index_mobs[mob.ID]["en-us"]) || mob.FriendlyName || mob.AegisName || `#${mob.ID}`;
const itemName = (item, lang) => (index_items[item.ID] && index_items[item.ID][lang]) || (index_items[item.ID] && index_items[item.ID]["en-us"]) || item.FriendlyName || item.NAME || item.ShortName || `#${item.ID}`;
const mapTitle = (mapname, lang) => { const m = index_maps[mapname]; const n = m && (m[lang] || m["en-us"]); return n && n !== "No Title" ? n : ""; };
const mapName = (mapname, lang) => { const n = mapTitle(mapname, lang); return n ? `${mapname} (${n})` : mapname; };       /* prt_fild01 (Prontera Field 01) */
const cityName = (mapname, lang) => mapTitle(mapname, lang) || mapname;                                                     /* Louyang */

/* THE DELTA. prev = previous episode data, cur = current episode data */
export const computeEpisodeDelta = ({ prev, cur, hideNoSpawn, hideNoDrop }) => {
  const prevMobIDs = new Set(prev.mobDB.map((m) => String(m.ID)));
  const prevItemIDs = new Set(prev.itemDB.map((i) => String(i.ID)));
  const curSpawnIDs = new Set(cur.spawn.map((s) => String(s.mob_id)));
  const prevSpawnMaps = new Set(prev.spawn.map((s) => s.mapname));
  const curSpawnMaps = new Set(cur.spawn.map((s) => s.mapname));

  const drops = (id) => cur.droppedBy[id] && cur.droppedBy[id].length > 0;

  let newMobs = cur.mobDB.filter((m) => !prevMobIDs.has(String(m.ID)));
  if (hideNoSpawn) newMobs = newMobs.filter((m) => curSpawnIDs.has(String(m.ID)));
  let newItems = cur.itemDB.filter((i) => !prevItemIDs.has(String(i.ID)));
  if (hideNoDrop) newItems = newItems.filter((i) => drops(i.ID));

  const newCards = newItems.filter((i) => isCardID(i.ID));
  const newMVPs = newMobs.filter((m) => mvps[m.ID] || (m.ExpPer && m.ExpPer !== "" && m.ExpPer !== "0"));
  const newMinibosses = newMobs.filter((m) => minibosses[m.ID]);
  const newMaps = [...curSpawnMaps].filter((m) => !prevSpawnMaps.has(m)).sort();
  const removedMaps = [...prevSpawnMaps].filter((m) => !curSpawnMaps.has(m)).sort();

  /* Cities come from worldmap.json (towns = first episode) - NOT from the DBs */
  const pPrev = epPos(prev.episode), pCur = epPos(cur.episode);
  const introducedBetween = (ep) => { const p = epPos(ep); return p > pPrev && p <= pCur; };
  const newCities = Object.entries(worldmapData.towns || {}).filter(([, ep]) => introducedBetween(ep)).map(([m]) => m);

  return { newMobs, newItems, newCards, newMVPs, newMinibosses, newMaps, removedMaps, newCities };
};

/* Manual note value: string | string[] | {"en-us": ..., "pt-br": ...} | array of those */
const loc = (v, lang) => {
  if (v === null || v === undefined) return [];
  if (Array.isArray(v)) return v.flatMap((x) => loc(x, lang));
  if (typeof v === "object") { const s = v[lang] || v["en-us"] || Object.values(v)[0]; return s ? [s] : []; }
  return String(v).trim() === "" ? [] : [String(v)];
};

/* HAND-WRITTEN PATCH NOTES (epi_diff.json) - the consolidated iRO patch-notes study.
   Ids match the app's episode ids (dashes); the app's Beta Era "0" aggregates the five beta rows.
   files_used.json is keyed by "Epi #" with the same ids; its own header-less columns name the data files behind each episode */
const EPIDIFF_BY_ID = Object.fromEntries(epiDiff.map((r) => [r.episode_id, r]));
const APP_TO_EPIDIFF = { "0": ["0-1", "0-2", "0-3", "0-4", "0-5"] };
export const getPatchNotesRows = (appEpisode) => (APP_TO_EPIDIFF[appEpisode] || [appEpisode]).map((id) => EPIDIFF_BY_ID[id]).filter(Boolean);
const FILES_USED_BY_ID = Object.fromEntries(filesUsed.map((r) => [r["Epi #"], r]));
export const getFilesUsed = (appEpisode) => (APP_TO_EPIDIFF[appEpisode] || [appEpisode]).map((id) => FILES_USED_BY_ID[id]).filter((r) => r && Object.entries(r).some(([k, v]) => k !== "Epi #" && k !== "Episode" && v && v.trim() !== ""));
const pnLines = (v) => (v || "").split("\n").map((t) => t.replace(/^- /, "").trim()).filter(Boolean);

/* Small shared bits */
const SectionTitle = ({ children, currentEpisode }) => <div className={`title-${currentEpisode} title-big`} style={{ marginTop: "28px" }}>{children}</div>;
const SubTitle = ({ children }) => <div style={{ fontWeight: "bold", marginTop: "8px", marginBottom: "2px" }}>{children}</div>;
/* The (AUTO) chip - deliberately loud so nobody mistakes a generated list for a hand-written one */
const AUTO_CHIP_STYLE = { display: "inline-block", fontSize: "11px", fontFamily: "OCRAStd", fontWeight: "bold", letterSpacing: "1px", lineHeight: "16px", marginLeft: "8px", padding: "1px 8px", borderRadius: "10px", background: "#ffcc00", color: "#000000", border: "1px solid #000000", boxShadow: "0 0 0 2px #ffcc00", verticalAlign: "middle" };
const MANUAL_CHIP_STYLE = { ...AUTO_CHIP_STYLE, background: "#b8b8b8", boxShadow: "0 0 0 2px #b8b8b8" };
/* Clickable toggle chip: yellow AUTO = calculated delta shown, gray MANUAL = hand-written patch notes shown */
const AutoTag = ({ prevEp, curEp, mode = "auto", onToggle }) => (
  <span
    title={(mode === "auto" ? `Calculated automatically: ${prevEp ? `${episodeLabel(prevEp)} vs ${episodeLabel(curEp)}` : ""}` : "Hand-written patch notes") + (onToggle ? " - click to switch" : "")}
    style={{ ...(mode === "auto" ? AUTO_CHIP_STYLE : MANUAL_CHIP_STYLE), cursor: onToggle ? "pointer" : "default" }}
    onClick={onToggle}
  >{mode === "auto" ? "AUTO" : "MANUAL"}</span>
);
const ManualList = ({ items, placeholder = true, plain = false }) => items.length === 0 ? (placeholder ? <div style={{ opacity: 0.6, fontStyle: "italic" }}>(no notes yet)</div> : null) : plain
  ? <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>{items.map((t, i) => <span key={i}>{t}</span>)}</div>
  : <ul style={{ margin: "2px 0 0 0", paddingLeft: "18px" }}>{items.map((t, i) => <li key={i}>{t}</li>)}</ul>;

/* Divergent-dates line: rendered VERBATIM - the data (epi_diff json/csv) is final, no post-processing.
   The only presentation step: a URL in the line becomes a clickable link whose text is the URL itself */
const DivergentLine = ({ text }) => {
  const m = text.match(/^(.*?)((?:https?|ftp):\/\/\S+)(.*)$/);
  if (m) return <div style={{ marginTop: "2px" }}>{m[1]}<a href={m[2]} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>{m[2]}</a>{m[3]}</div>;
  return <div style={{ marginTop: "2px" }}>{text}</div>;
};

/* Long lists collapse behind a <details> so 3000 new items don't flood the window. One entry per line */
const COLLAPSE_AT = 40;
const EntityList = ({ entries, onClick }) => {
  if (entries.length === 0) return <div style={{ opacity: 0.6, fontStyle: "italic" }}>none</div>;
  const list = (
    <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
      {entries.map((e) => (
        <span key={e.id} className={onClick ? "spawn-mob-link" : ""} style={{ cursor: onClick ? "pointer" : "default" }} onClick={onClick ? () => onClick(e.id) : undefined}>
          {e.label}
        </span>
      ))}
    </div>
  );
  if (entries.length <= COLLAPSE_AT) return list;
  return <details><summary style={{ cursor: "pointer" }}>show all {entries.length}</summary>{list}</details>;
};

export default function EpisodeDetailsView({
  currentEpisode, currentLanguage, availableEpisodes,
  currentEpisodeMobDB, currentEpisodeItemDB, currentEpisodeMobSpawn, currentEpisodeDroppedBy,
  hideNoSpawn, sethideNoSpawn, hideNoDrop, sethideNoDrop,
  onMobClick, onItemClick,
}) {
  const prevEpisode = getPreviousEpisode(currentEpisode, availableEpisodes);
  const prev = usePreviousEpisodeData(prevEpisode);
  const notes = getManualNotes(currentEpisode) || {};
  const lang = currentLanguage;

  const delta = useMemo(() => {
    if (!prev || prev.error) return null;
    return computeEpisodeDelta({
      prev,
      cur: { episode: currentEpisode, mobDB: currentEpisodeMobDB, itemDB: currentEpisodeItemDB, spawn: currentEpisodeMobSpawn, droppedBy: currentEpisodeDroppedBy },
      hideNoSpawn, hideNoDrop,
    });
  }, [prev, currentEpisode, currentEpisodeMobDB, currentEpisodeItemDB, currentEpisodeMobSpawn, currentEpisodeDroppedBy, hideNoSpawn, hideNoDrop]);

  const mobEntries = (list) => list.map((m) => ({ id: m.ID, label: `${m.ID} - ${mobName(m, lang)}` }));
  const itemEntries = (list) => list.map((i) => ({ id: i.ID, label: `${i.ID} - ${itemName(i, lang)}` }));
  const cardEntries = (list) => list.map((i) => {
    const by = currentEpisodeDroppedBy[i.ID] || [];
    const from = by.length > 0 ? ` (${by.map((b) => b.mob).join(", ")})` : "";
    return { id: i.ID, label: `${i.ID} - ${itemName(i, lang)}${from}` };
  });
  const Auto = () => prevEpisode !== null ? <AutoTag prevEp={prevEpisode} curEp={currentEpisode} mode={showAuto ? "auto" : "manual"} onToggle={() => setshowAuto((v) => !v)} /> : null;
  const none = <div style={{ opacity: 0.6, fontStyle: "italic" }}>none</div>;
  const manualOr = (items) => items.length > 0 ? <ManualList items={items} placeholder={false} plain /> : none;
  const rag = notes.ragnadb || {};
  const mods = rag.modifications || {};

  /* Hand-written patch notes (epi_diff.json) + data files used (files_used.json) */
  const [showAuto, setshowAuto] = useState(true); /* AUTO/MANUAL toggle - flipped by clicking any tag chip */
  const pnRows = getPatchNotesRows(currentEpisode);
  const pn = (field) => pnRows.flatMap((r) => pnLines(r[field]).map((t) => (pnRows.length > 1 ? `[${r.episode}] ${t}` : t)));
  const fuRows = getFilesUsed(currentEpisode);

  return (
    <div style={{ textAlign: "left" }}>

{/* // ABOUT THE <AUTO> TAG - floating box glued to the right of the window; the text flows around it */}
      {prevEpisode !== null && (
        <div style={{ float: "right", width: "300px", maxWidth: "45%", margin: "0 0 10px 14px", padding: "10px", border: "2px solid #ffcc00", borderRadius: "4px", fontSize: "12px", background: "rgba(0,0,0,0.35)" }}>
          <div style={{ fontSize: "21px", fontWeight: "bold", lineHeight: "1.3", color: "#ffffff" }}>
            <span style={{ ...AUTO_CHIP_STYLE, marginLeft: 0, fontSize: "17px", lineHeight: "24px" }}>AUTO</span> showing delta:
            <div style={{ color: "#ffd866" }}>{episodeLabel(prevEpisode)}</div>
            <div style={{ textAlign: "center", lineHeight: "1" }}>&#8597;</div>{/* vertical two-headed arrow */}
            <div style={{ color: "#8be9fd" }}>{episodeLabel(currentEpisode)}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", margin: "10px 0", paddingTop: "8px", borderTop: "1px solid rgba(255,204,0,0.5)", color: "#ffffff" }}>{/* the two filters that drive the delta - same global state as the left panel */}
            <label style={{ cursor: "pointer" }}><input type="checkbox" checked={hideNoSpawn} onChange={() => sethideNoSpawn((v) => !v)} /> Hide Mobs without spawn from deltas</label>
            <label style={{ cursor: "pointer" }}><input type="checkbox" checked={hideNoDrop} onChange={() => sethideNoDrop((v) => !v)} /> Hide Items that don't drop from deltas</label>
          </div>
          <div style={{ marginTop: "18px", fontWeight: "bold" }}>About the AUTO tag</div>
          <div style={{ marginTop: "4px" }}>When this tag is present, it means the field was generated in an automated way. <b>AUTO</b> fields are calculated and provide the delta (changes; differences) between the closest earlier episode available and the current one you're exploring.</div>
          <div style={{ marginTop: "6px" }}>This means that, if this gap is greater than 1 episode, this delta will not show only the differences from the immediate earlier ("x-1") episode, but all differences that are found since the earliest <b>AVAILABLE</b>.</div>
          <div style={{ marginTop: "6px" }}>All other fields (no <b>AUTO</b> tag present) refer to changes in the current episode. So these, yes, do speak of changes from the last "x-1" episode to the current.</div>
          <div style={{ marginTop: "6px" }}>Click the tag on any section to switch it between the calculated delta (<b>AUTO</b>, yellow) and the hand-written patch notes (<b>MANUAL</b>, gray).</div>
          {!prev && <div style={{ marginTop: "4px", opacity: 0.7 }}>loading {episodeLabel(prevEpisode)}...</div>}
          {prev && prev.error && <div style={{ marginTop: "4px" }}>Could not load {episodeLabel(prevEpisode)} data.</div>}
        </div>
      )}

{/* // HEADER */}
      <div style={{ fontWeight: "bold", fontSize: "16px" }}>{episodeLabel(currentEpisode)}</div>
      {prevEpisode === null && <div style={{ fontSize: "12px", marginTop: "4px" }}>No earlier episode is available in RagnaDB, so there is nothing to compare {episodeLabel(currentEpisode)} against.</div>}

{/* // DATES (hand-written, epi_diff.json) */}
      {pnRows.map((r) => (
        <div key={r.episode_id} style={{ fontSize: "16px", marginTop: "4px" }}>{/* same size as the episode title line */}
          <b>{pnRows.length > 1 ? `${r.episode}: ` : "Release Date: "}</b>{r.date}
          {r.other_dates && <div style={{ fontSize: "12px" }}>{pnLines(r.other_dates).map((t, i) => <div key={i}>{t}</div>)}</div>}
          {r.divergent_dates && (
            <details style={{ fontSize: "12px" }}>{/* triangle flush left; opened lines aligned with the "d" of divergent */}
              <summary style={{ cursor: "pointer", opacity: 0.8 }}>divergent dates across sources</summary>
              <div style={{ paddingLeft: "1.1em", marginTop: "2px", overflowWrap: "anywhere" }}>{pnLines(r.divergent_dates).map((t, i) => <DivergentLine key={i} text={t} />)}</div>
            </details>
          )}
        </div>
      ))}

{/* // HISTORICAL STUFF / TRIVIA (manual) */}
      <SectionTitle currentEpisode={currentEpisode}>Historical Stuff / Interesting Facts / Trivia</SectionTitle>
      <ManualList items={loc(notes.trivia, lang)} />

{/* // GENERAL UPDATES / NEW GAME FEATURES (manual) */}
      <SectionTitle currentEpisode={currentEpisode}>General updates / New game features</SectionTitle>
      <ManualList items={[...pn("new_features"), ...loc(notes.features, lang)]} />

{/* // NEW CITIES (auto from worldmap) + relevant changes (manual) */}
      <SectionTitle currentEpisode={currentEpisode}>New cities<Auto /></SectionTitle>
      {showAuto ? (delta ? <EntityList entries={delta.newCities.map((m) => ({ id: m, label: cityName(m, lang) }))} /> : <div style={{ opacity: 0.6 }}>-</div>)
       : manualOr([...pn("new_cities"), ...loc(notes.cities && notes.cities.changes, lang)])}

{/* // NEW DUNGEONS (hand-written) */}
      {pn("new_dungeons").length > 0 && (<>
      <SectionTitle currentEpisode={currentEpisode}>New dungeons</SectionTitle>
      <ManualList items={pn("new_dungeons")} placeholder={false} plain />
      </>)}

{/* // NEW MAPS (auto) */}
      <SectionTitle currentEpisode={currentEpisode}>New maps<Auto /></SectionTitle>
      {showAuto ? (delta ? <EntityList entries={delta.newMaps.map((m) => ({ id: m, label: mapName(m, lang) }))} /> : <div style={{ opacity: 0.6 }}>-</div>)
       : manualOr([...pn("new_maps"), ...loc(notes.maps, lang)])}

{/* // SKILL MODIFICATIONS (manual) */}
      <SectionTitle currentEpisode={currentEpisode}>Skill modifications</SectionTitle>
      <ManualList items={loc(notes.skills, lang)} />

{/* // NEW QUESTS (hand-written) */}
      {pn("new_quests").length > 0 && (<>
      <SectionTitle currentEpisode={currentEpisode}>New quests</SectionTitle>
      <ManualList items={pn("new_quests")} placeholder={false} plain />
      </>)}

{/* // RAGNADB NOTES (manual) - before the long lists */}
      <SectionTitle currentEpisode={currentEpisode}>Ragnadb notes</SectionTitle>
      <table style={{ borderCollapse: "collapse", fontSize: "12px" }}><tbody>
        {loc(rag.server_used, lang).length > 0 && <tr><td style={{ paddingRight: "12px", fontWeight: "bold" }}>Server Used (mob &amp; item DBs):</td><td>{loc(rag.server_used, lang).join("; ")}</td></tr>}
        {loc(rag.client_used, lang).length > 0 && <tr><td style={{ paddingRight: "12px", fontWeight: "bold" }}>Client Used (Item description):</td><td>{loc(rag.client_used, lang).join("; ")}</td></tr>}
        {loc(rag.spawn_used, lang).length > 0 && <tr><td style={{ paddingRight: "12px", fontWeight: "bold" }}>Spawn Used:</td><td>{loc(rag.spawn_used, lang).join("; ")}</td></tr>}
        {fuRows.map((r, i) => (<React.Fragment key={i}>{/* files_used.json - one block per episode row (Beta Era shows several) */}
          {fuRows.length > 1 && <tr><td colSpan={2} style={{ fontWeight: "bold", paddingTop: "6px" }}>{r["Episode"]}</td></tr>}
          {[["mob_db", "Mobs from"], ["item_db", "Items from"], ["mob.sc", "Mob spawn from"], ["item_description (idnum2itemdesctable)", "Item descriptions from"], ["item_res2 [id <> sprite] (idnum2itemresnametable", "Item sprites from"], ["map (folder)", "Maps from"], ["Status", "Status"]].map(([k, label]) =>
            r[k] ? <tr key={k}><td style={{ paddingRight: "12px", fontWeight: "bold", verticalAlign: "top" }}>{label}:</td><td style={{ whiteSpace: "pre-line" }}>{r[k]}</td></tr> : null)}
        </React.Fragment>))}
      </tbody></table>
      {fuRows.some((r) => r["Server Notes"] || r["Client Notes"] || r["Mob.sc Notes"]) && (<>
      <SubTitle>Data file notes</SubTitle>
      {fuRows.map((r, i) => (<React.Fragment key={i}>
        {["Server Notes", "Client Notes", "Mob.sc Notes"].map((k) => r[k] ? (
          <details key={k} style={{ fontSize: "12px", marginTop: "4px" }}>
            <summary style={{ cursor: "pointer", fontWeight: "bold" }}>{fuRows.length > 1 ? `${r["Episode"]} - ` : ""}{k}</summary>
            <div style={{ whiteSpace: "pre-line", paddingLeft: "14px" }}>{r[k]}</div>
          </details>
        ) : null)}
      </React.Fragment>))}
      </>)}
      {(loc(mods.server, lang).length > 0 || loc(mods.client, lang).length > 0 || loc(mods.spawn, lang).length > 0) && (<>{/* only when there ARE modifications */}
      <SubTitle>Modifications</SubTitle>
      {loc(mods.server, lang).length > 0 && (<><div style={{ fontWeight: "bold", fontSize: "12px" }}>Server Notes</div><ManualList items={loc(mods.server, lang)} /></>)}
      {loc(mods.client, lang).length > 0 && (<><div style={{ fontWeight: "bold", fontSize: "12px", marginTop: "6px" }}>Client Notes</div><ManualList items={loc(mods.client, lang)} /></>)}
      {loc(mods.spawn, lang).length > 0 && (<><div style={{ fontWeight: "bold", fontSize: "12px", marginTop: "6px" }}>Spawn Notes</div><ManualList items={loc(mods.spawn, lang)} /></>)}
      </>)}

{/* // MOB RELOCATION (manual) */}
      <SectionTitle currentEpisode={currentEpisode}>Mob relocation</SectionTitle>
      <ManualList items={[...pn("mob_relocation"), ...loc(notes.mobs && notes.mobs.relocation, lang)]} />

{/* // NEW CARDS (auto) - card updates are reported by hand under General updates */}
      <SectionTitle currentEpisode={currentEpisode}>New cards{showAuto ? (delta ? ` (${delta.newCards.length})` : "") : ` (${pn("new_cards").length})`}<Auto /></SectionTitle>
      {showAuto ? (delta ? <EntityList entries={cardEntries(delta.newCards)} onClick={onItemClick} /> : <div style={{ opacity: 0.6 }}>-</div>)
       : manualOr(pn("new_cards"))}

{/* // MOBS (auto: MVPs / minibosses / mobs) */}
      <SectionTitle currentEpisode={currentEpisode}>New MVPs{showAuto ? (delta ? ` (${delta.newMVPs.length})` : "") : ` (${pn("new_mvps").length})`}<Auto /></SectionTitle>
      {showAuto ? (delta ? <EntityList entries={mobEntries(delta.newMVPs)} onClick={onMobClick} /> : <div style={{ opacity: 0.6 }}>-</div>)
       : manualOr(pn("new_mvps"))}
      {showAuto && (<>
      <SectionTitle currentEpisode={currentEpisode}>New minibosses{delta ? ` (${delta.newMinibosses.length})` : ""}<Auto /></SectionTitle>
      {delta ? <EntityList entries={mobEntries(delta.newMinibosses)} onClick={onMobClick} /> : <div style={{ opacity: 0.6 }}>-</div>}
      </>)}
      <SectionTitle currentEpisode={currentEpisode}>New mobs{showAuto ? (delta ? ` (${delta.newMobs.length})` : "") : ` (${pn("new_mobs").length})`}<Auto /></SectionTitle>
      {showAuto ? (delta ? <EntityList entries={mobEntries(delta.newMobs)} onClick={onMobClick} /> : <div style={{ opacity: 0.6 }}>-</div>)
       : manualOr(pn("new_mobs"))}

{/* // ITEMS (auto) - longest list, last */}
      <SectionTitle currentEpisode={currentEpisode}>New items{showAuto ? (delta ? ` (${delta.newItems.length})` : "") : ` (${loc(notes.items, lang).length})`}<Auto /></SectionTitle>
      {showAuto ? (delta ? <EntityList entries={itemEntries(delta.newItems)} onClick={onItemClick} /> : <div style={{ opacity: 0.6 }}>-</div>)
       : manualOr(loc(notes.items, lang))}

{/* // SOURCES (hand-written, epi_diff.json) - each line is "<url> - what was extracted" */}
      {(pn("sources").length > 0 || pn("old_patch_client_files").length > 0) && (<>
      <SectionTitle currentEpisode={currentEpisode}>Sources</SectionTitle>
      <ul style={{ margin: "2px 0 0 0", paddingLeft: "18px", fontSize: "12px", overflowWrap: "anywhere" }}>
        {pn("sources").map((t, i) => {
          const m = t.match(/^(\[[^\]]+\] )?((?:https?|ftp):\/\/\S+)( - )?(.*)$/);
          return <li key={i}>{m ? <>{m[1] || ""}<a href={m[2]} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>{m[2]}</a>{m[4] ? ` - ${m[4]}` : ""}</> : t}</li>;
        })}
      </ul>
      {pn("old_patch_client_files").length > 0 && (<>
      <SubTitle>Old patch / client files</SubTitle>
      <ul style={{ margin: "2px 0 0 0", paddingLeft: "18px", fontSize: "12px", overflowWrap: "anywhere" }}>
        {pn("old_patch_client_files").map((t, i) => {
          const m = t.match(/^(\[[^\]]+\] )?((?:https?|ftp):\/\/\S+)( - )?(.*)$/);
          return <li key={i}>{m ? <>{m[1] || ""}<a href={m[2]} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>{m[2]}</a>{m[4] ? ` - ${m[4]}` : ""}</> : t}</li>;
        })}
      </ul>
      </>)}
      </>)}

{/* // OTHER (auto) - leftovers that don't deserve a section of their own */}
      {showAuto && (<>
      <SectionTitle currentEpisode={currentEpisode}>Other</SectionTitle>
      <SubTitle>Maps that lost all spawns<Auto /></SubTitle>
      {delta ? <EntityList entries={delta.removedMaps.map((m) => ({ id: m, label: mapName(m, lang) }))} /> : <div style={{ opacity: 0.6 }}>-</div>}
      </>)}

    </div>
  );
}
